import { QuotationResponse } from "@/apis/sangjoQuotation";

import { matchRequestResDataState } from "@/atoms/match/matchRequestResDataState";
import useSangjoQuotationRequests from "@/hooks/useSangjoQuotationRequests";
import { QuotationRequestBody } from "@/types/quotationType";
import { Box } from "@chakra-ui/react";
import { useOverlay } from "@toss/use-overlay";
import { AxiosResponse } from "axios";
import { 조문객예상인원_LIST } from "containers/MatchContainer/constants/request";
import {
  customEvent,
  Dropdown,
  Field,
  FUNERAL_SIDO_LIST,
  FUNERAL_SIGUNGU,
  HackleEventName,
  HStack,
  LargeButton,
  LARGEL_BUTTON_TYPE,
  OverlayModal,
  responsive,
  Spacing,
  VStack,
} from "goi_common";
import { Reducer, useMemo, useReducer } from "react";
import { useSetRecoilState } from "recoil";
import CustomRequestModalContent from "@/components/Match/CustomRequestModalContent";
import { fireGuestSubmitEvents } from "@/containers/MatchContainer/fireGuestSubmitEvents";

const 조문객수ServiceType = {
  "0명(무빈소)": "무빈소",
  "50명 내외": "가족장",
  "100명 내외": "가족장",
  "200명 이상": "일반장",
  모르겠어요: "가족장",
};

const SIDO_LIST = FUNERAL_SIDO_LIST.map((sido) => ({
  key: sido[1] as string,
  value: sido[0],
}));

const 장례준비긴급도_LIST = [
  "급하지 않지만 미리 알아보려고 해요.",
  "임종이 며칠 남지 않았어요.",
  "임종하신 상태입니다.",
];

export interface SelectedOption {
  장례준비긴급도: string;
  sido: null | number;
  sigungu: null | number;
  조문객수: string;
}

function AllQuotationRequestClosedVersion() {
  const [selectedOption, setSelectedOption] = useReducer<
    Reducer<SelectedOption, Partial<SelectedOption>>
  >(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      장례준비긴급도: "",
      sido: null,
      sigungu: null,
      조문객수: "",
    },
  );

  const showSigunguGuideMessage = useMemo(() => {
    return selectedOption.sido && !selectedOption.sigungu;
  }, [selectedOption]);

  const sigunguValues = useMemo(() => {
    if (!selectedOption.sido) {
      return [];
    }

    const currentSigunguList = Array.from(FUNERAL_SIGUNGU.get(selectedOption.sido)!);

    return currentSigunguList.map((sigungu) => {
      const [code, name] = sigungu;

      return {
        key: name,
        value: code,
      };
    });
  }, [selectedOption.sido]);

  return (
    <>
      <VStack width="100%">
        <VStack padding="0 24px">
          <Field.Radio
            title="장례 준비가 긴급한 상황일까요?"
            items={장례준비긴급도_LIST}
            value={selectedOption.장례준비긴급도}
            onChange={(e) => {
              setSelectedOption({ 장례준비긴급도: e });
              customEvent({
                newGtm: {
                  path: "match_clsd_req",
                  trigger_and_target: "status-answer",
                },
              });
            }}
            radioWidth="100%"
            flexDirection="column"
          />
          <Spacing gap={36} />
          <Field.Radio
            title="조문객의 수는 얼마나 예상하시나요?"
            items={조문객예상인원_LIST}
            value={selectedOption.조문객수}
            onChange={(e) => {
              setSelectedOption({ 조문객수: e });
              customEvent({
                newGtm: {
                  path: "match_clsd_req",
                  trigger_and_target: "condolers-answer",
                },
              });
            }}
            radioWidth="100%"
            flexDirection="column"
          />
          <Spacing gap={36} />
          <VStack gap="16px" position="relative">
            <Box className="subtitle2_sb gray900">어디에서 장례를 치를 계획이신가요?</Box>
            <HStack gap="12px" zIndex={0}>
              <Dropdown
                name="지역"
                value={selectedOption.sido}
                values={SIDO_LIST}
                onChange={(v) => {
                  setSelectedOption({ sido: v, sigungu: undefined });
                  customEvent({
                    newGtm: {
                      path: "match_clsd_req",
                      trigger_and_target: "sido-answer",
                    },
                  });
                }}
              />
              <Dropdown
                name="지역"
                value={selectedOption.sigungu}
                values={sigunguValues}
                onChange={(v) => {
                  setSelectedOption({ sigungu: v });
                  customEvent({
                    newGtm: {
                      path: "match_clsd_req",
                      trigger_and_target: "sigungu-answer",
                    },
                  });
                }}
              />
            </HStack>
            {showSigunguGuideMessage && (
              <div
                css={{ position: "absolute", bottom: -30, left: 5, zIndex: -1 }}
                className="orange650 button"
              >
                다음 지역을 선택해주세요.
              </div>
            )}
          </VStack>

          <Spacing gap={360} />
        </VStack>
      </VStack>
      <AllQuotationRequestClosedVersionButton selectedOption={selectedOption} />
    </>
  );
}

export default AllQuotationRequestClosedVersion;

function AllQuotationRequestClosedVersionButton({
  selectedOption,
}: {
  selectedOption: SelectedOption;
}) {
  const setResponseData = useSetRecoilState<any>(matchRequestResDataState);

  const { setQuotationRequestCookie, mutateSangjoQuotation, isLoading } =
    useSangjoQuotationRequests();
  const overlay = useOverlay();

  const isDisabled =
    !selectedOption.장례준비긴급도 ||
    !selectedOption.sido ||
    !selectedOption.sigungu ||
    !selectedOption.조문객수;

  const sendConversionEvent = () => {
    customEvent({
      newGtm: {
        path: "match_clsd_req",
        trigger_and_target: "submit_conversion",
      },
      hackle: { key: HackleEventName["견적 요청 제출 클릭"] },
    });
  };

  const parseCondolers = (조문객수: string) => {
    ``;
    if (조문객수 === "없음") {
      return "";
    }

    if (조문객수 === "0명(무빈소)") {
      return "0명";
    }

    return 조문객수;
  };

  const parseRequestBody = (selectedOption: SelectedOption): QuotationRequestBody => {
    return {
      status: selectedOption.장례준비긴급도,
      funeral_sido: selectedOption.sido as number,
      funeral_sigungu: selectedOption.sigungu as number,
      needs_binso: selectedOption.조문객수 === "0명(무빈소)" ? false : true,
      condolers: parseCondolers(selectedOption.조문객수),
      is_sequential: false,
    };
  };

  const handleSubmit = async () => {
    if (isLoading) {
      return;
    }

    sendConversionEvent();

    const body = parseRequestBody(selectedOption);

    const onSuccess = ({ data }: AxiosResponse<QuotationResponse>) => {
      customEvent({
        newGtm: {
          action: "match_request_complete",
        },
      });
      fireGuestSubmitEvents(data?.status);

      setResponseData(data);
      setQuotationRequestCookie(data);

      overlay.open(({ isOpen, close: onClose }) => {
        return (
          <OverlayModal
            onClose={onClose}
            isOpen={isOpen}
            modalContentStyle={{
              minWidth: "320px",
              overflow: "scroll",
            }}
            overlayModalContent={
              <CustomRequestModalContent
                eventFrom="uv_closed_rqst"
                serviceType={
                  조문객수ServiceType[selectedOption.조문객수 as keyof typeof 조문객수ServiceType]
                }
                onClose={onClose}
              />
            }
          />
        );
      });
    };

    mutateSangjoQuotation({
      body,
      onSuccess,
    });
  };

  return (
    <Box
      position={"fixed"}
      bottom="0"
      css={responsive({
        width: { md: "480px", base: "100%" },
        maxWidth: { md: "var(--global-max-width)", base: "480px" },
      })}
    >
      <LargeButton
        buttonType={isDisabled ? LARGEL_BUTTON_TYPE.ORANGE_DISABLED : LARGEL_BUTTON_TYPE.ORANGE}
        disabled={isDisabled}
        height={60}
        onClick={handleSubmit}
      >
        <Box display="flex" maxWidth="300px">
          장례 준비 도움받기
        </Box>
      </LargeButton>
    </Box>
  );
}
